import { plural, t } from '@lingui/core/macro';
import { LeadGenerationRequester, TireLeadReason, type TireQuoteCustomerLeadRecord, TireSeason } from '@navio/nsdk';
import { formatMm } from './formatMm';

export type TireLeadSeverity = 'high' | 'medium' | 'info';

export type TireLeadReasonCopy = {
  headline: string;
  severity: TireLeadSeverity;
  /** Short consequences listed on the status card. */
  risks: string[];
  whyIntro: string;
  whyPoints: { title: string; description: string }[];
};

// Most severe first: the card explains the worst finding when tires have different reasons.
const REASON_PRIORITY: TireLeadReason[] = [
  TireLeadReason.SIDEWALL_DAMAGE,
  TireLeadReason.PUNCTURE,
  TireLeadReason.CRACKS_DRY_ROT,
  TireLeadReason.UNEVEN_WEAR,
  TireLeadReason.OLD_TIRES,
  TireLeadReason.OTHER,
  TireLeadReason.WORN_TREAD,
  TireLeadReason.CUSTOMER_WISH
];

/** The reason the offer page leads with: a customer's own request, else the most severe finding. */
export const getPrimaryTireLeadReason = (lead: TireQuoteCustomerLeadRecord): TireLeadReason => {
  if (lead.requestedBy === LeadGenerationRequester.CUSTOMER) {
    return TireLeadReason.CUSTOMER_WISH;
  }
  const reasons = new Set(lead.tireDetails.positions.flatMap((position) => position.reasons));
  return REASON_PRIORITY.find((reason) => reasons.has(reason)) ?? TireLeadReason.OTHER;
};

/** What worn tread copy depends on: winter tires wear into other risks, and below the legal limit they must go. */
export type WornTreadContext = {
  season: TireSeason;
  limitMm: number | null;
  belowLegalLimit: boolean;
};

/** Customer copy for the offer page, by the reason the worker recorded. `tireCount` is the number of tires with that reason. */
export const getTireLeadReasonCopy = (
  reason: TireLeadReason,
  tireCount: number,
  wornTread: WornTreadContext
): TireLeadReasonCopy => {
  const pairNote = {
    title: t`We replace tires in pairs`,
    description: t`Both tires on the same axle should have the same grip, so we recommend replacing them together.`
  };
  switch (reason) {
    case TireLeadReason.WORN_TREAD:
      return getWornTreadCopy(wornTread);
    case TireLeadReason.UNEVEN_WEAR:
      return {
        headline: plural(tireCount, { one: 'A tire is worn unevenly', other: 'Your tires are worn unevenly' }),
        severity: 'medium',
        risks: [
          t`Part of the tread is almost worn down`,
          t`Less grip on wet roads`,
          t`Often caused by wheel alignment`
        ],
        whyIntro: t`When a tire wears unevenly, one part of the tread can be almost gone even if the rest looks fine. The most worn part decides how well the tire grips.`,
        whyPoints: [
          {
            title: t`Less grip`,
            description: t`The worn part clears less water and gives a longer braking distance on wet roads.`
          },
          {
            title: t`Often wheel alignment`,
            description: t`Uneven wear is often caused by wrong wheel alignment. We recommend checking it when new tires go on.`
          },
          {
            title: t`Wears faster`,
            description: t`Without a fix, new tires also wear unevenly.`
          }
        ]
      };
    case TireLeadReason.CRACKS_DRY_ROT:
      return {
        headline: plural(tireCount, {
          one: 'A tire has cracks in the rubber',
          other: 'Your tires have cracks in the rubber'
        }),
        severity: 'medium',
        risks: [t`Dried-out rubber grips less`, t`The cracks can grow and leak air`, t`Cannot be repaired`],
        whyIntro: t`Cracks come from the rubber drying out with age, sun and cold. They weaken the tire even when the tread is deep enough.`,
        whyPoints: [
          {
            title: t`Less grip`,
            description: t`Hard, cracked rubber grips less, especially on wet and cold roads.`
          },
          {
            title: t`Risk of air leaks`,
            description: t`The cracks can grow into the tire and let the air out.`
          },
          {
            title: t`Cannot be repaired`,
            description: t`Cracks in the rubber cannot be fixed. The tire has to be replaced.`
          }
        ]
      };
    case TireLeadReason.SIDEWALL_DAMAGE:
      return {
        headline: plural(tireCount, {
          one: 'A tire has damage in the sidewall',
          other: '# tires have damage in the sidewall'
        }),
        severity: 'high',
        risks: [
          t`The tire can burst suddenly at speed`,
          t`Sidewall damage cannot be repaired`,
          t`Replace it before driving far`
        ],
        whyIntro: t`The sidewall carries the weight of the car and flexes all the time while you drive. Damage there weakens the tire, and it can burst without warning.`,
        whyPoints: [
          {
            title: t`Risk of a sudden blowout`,
            description: t`A weakened sidewall can give way at speed or when hitting a curb.`
          },
          {
            title: t`Cannot be repaired`,
            description: t`Sidewall damage cannot be patched. The tire has to be replaced.`
          },
          pairNote
        ]
      };
    case TireLeadReason.PUNCTURE:
      return {
        headline: plural(tireCount, { one: 'A tire is punctured', other: '# tires are punctured' }),
        severity: 'high',
        risks: [
          t`The tire loses air and can fail`,
          t`The puncture cannot be repaired safely`,
          t`Low pressure means longer braking distance`
        ],
        // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
        whyIntro: t`The technician found that the puncture cannot be repaired safely, for example because it is close to the sidewall or too large.`,
        whyPoints: [
          {
            title: t`Risk of losing air`,
            description: t`The tire can lose air slowly or all at once.`
          },
          {
            title: t`Worse handling`,
            description: t`Low tire pressure gives a longer braking distance and worse steering.`
          },
          pairNote
        ]
      };
    case TireLeadReason.OLD_TIRES:
      return {
        headline: t`Your tires are old`,
        severity: 'medium',
        risks: [t`The rubber hardens and grips less`, t`Even when the tread looks fine`, t`Higher risk of cracks`],
        whyIntro: t`Rubber ages even when the tire is not driven much, and becomes harder and less elastic over time.`,
        whyPoints: [
          {
            title: t`Less grip`,
            description: t`Hard rubber grips less, especially on wet and cold roads.`
          },
          {
            title: t`Tread is not everything`,
            description: t`An old tire can have good tread depth and still give a longer braking distance.`
          },
          {
            title: t`Manufacturers' advice`,
            description: t`Many tire makers recommend replacing tires after 6 to 10 years, whatever the tread depth.`
          }
        ]
      };
    case TireLeadReason.OTHER:
      return {
        headline: plural(tireCount, {
          // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
          one: 'The technician found a fault on a tire',
          // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
          other: 'The technician found faults on # tires'
        }),
        severity: 'medium',
        risks: [],
        // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
        whyIntro: t`The technician judged that the fault makes the tire less safe to drive on.`,
        whyPoints: [
          {
            // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
            title: t`Checked by the technician`,
            description: t`The fault was found during your tire change.`
          },
          pairNote
        ]
      };
    case TireLeadReason.CUSTOMER_WISH:
      return {
        headline: t`Here is the offer you asked for`,
        severity: 'info',
        risks: [],
        whyIntro: '',
        whyPoints: []
      };
  }
};

// Claude Design "Tire Offer": winter tires lose grip on snow, ice and slush; summer tires on wet roads.
const getWornTreadCopy = ({ season, limitMm, belowLegalLimit }: WornTreadContext): TireLeadReasonCopy => {
  const isWinter = season === TireSeason.WINTER;
  const braking = isWinter
    ? {
        title: t`Longer braking distance on snow and ice`,
        description: t`When the tread gets shallow, it bites less into the surface. Braking distance grows well before the tire reaches the legal limit.`
      }
    : {
        title: t`Longer braking distance on wet roads`,
        description: t`Shallow tread clears less water. Braking distance on wet roads grows well before the tire reaches the legal limit.`
      };
  const grip = isWinter
    ? {
        title: t`Less grip in sleet and slush`,
        description: t`Shallow tread does not push slush and water away. The car can slide, even at low speed.`
      }
    : {
        title: t`Higher risk of aquaplaning`,
        description: t`When the tire cannot push the water away, the car loses contact with the road and you lose steering.`
      };

  return {
    headline: belowLegalLimit ? t`Your tires must be replaced` : t`Your tires should be replaced`,
    severity: belowLegalLimit ? 'high' : 'medium',
    risks: [braking.title, grip.title],
    whyIntro: getWornTreadWhyIntro(isWinter, limitMm, belowLegalLimit),
    whyPoints: [
      braking,
      grip,
      {
        title: t`Fines and insurance`,
        description: t`Driving on tires below the legal limit can mean a fine, and your insurer can reduce the payout after an accident.`
      }
    ]
  };
};

const getWornTreadWhyIntro = (isWinter: boolean, limitMm: number | null, belowLegalLimit: boolean) => {
  const limit = limitMm === null ? null : formatMm(limitMm);
  if (belowLegalLimit && limit !== null) {
    return isWinter
      ? t`Your tires are below the legal limit of ${limit} mm for winter tires. It is illegal to drive on them, and they should be replaced before you drive again.`
      : t`Your tires are below the legal limit of ${limit} mm. It is illegal to drive on them, and they should be replaced before you drive again.`;
  }
  if (isWinter) {
    return limit === null
      ? t`Winter tires lose their grip on snow and ice long before they reach the legal limit.`
      : t`Winter tires lose their grip on snow and ice long before they reach the legal limit. Your tires are close to the limit of ${limit} mm that applies in the winter season.`;
  }
  return t`The legal limit says when a tire becomes illegal, not when it stops being safe. Your tires have lost much of their grip, and waiting will not make it better.`;
};
